import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  normalizeEntityPermissions,
  useGetMyAdminAccessQuery,
  type EntityPermissionKey,
  type PageAccess,
  type PageAccessKey,
  type ScopeMode,
} from '@/entities/Administration';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import { routerPath } from '@/shared/config/router';
import { isPathAllowed } from './pathnamePageAccess';

export type EntityCrudAction = 'create' | 'update' | 'delete';

type AdminAccessContextValue = {
  isLoading: boolean;
  isError: boolean;
  pageAccess: PageAccess | null;
  scopeMode: ScopeMode | null;
  canAccessPage: (key: PageAccessKey) => boolean;
  canEntity: (key: EntityPermissionKey, action: EntityCrudAction) => boolean;
};

const AdminAccessContext = createContext<AdminAccessContextValue | null>(null);

export function AdminAccessProvider({ children }: { children: ReactNode }) {
  const token = LocalStorageService.getAccessToken();
  const { data, isLoading, isError } = useGetMyAdminAccessQuery(undefined, { skip: !token });
  const location = useLocation();
  const navigate = useNavigate();

  const pageAccess = data?.data?.pageAccess ?? null;
  const scopeMode = data?.data?.scopeMode ?? null;
  const entityPermissions = useMemo(
    () => normalizeEntityPermissions(data?.data?.entityPermissions),
    [data?.data?.entityPermissions],
  );

  const canAccessPage = useCallback(
    (key: PageAccessKey) => pageAccess?.[key] === true,
    [pageAccess],
  );

  const canEntity = useCallback(
    (key: EntityPermissionKey, action: EntityCrudAction) =>
      entityPermissions?.[key]?.[action] === true,
    [entityPermissions],
  );

  useEffect(() => {
    if (isLoading || !pageAccess) return;
    if (isPathAllowed(location.pathname, pageAccess)) return;
    navigate(routerPath.main, { replace: true });
  }, [isLoading, pageAccess, location.pathname, navigate]);

  const value = useMemo<AdminAccessContextValue>(
    () => ({
      isLoading,
      isError,
      pageAccess,
      scopeMode,
      canAccessPage,
      canEntity,
    }),
    [isLoading, isError, pageAccess, scopeMode, canAccessPage, canEntity],
  );

  return <AdminAccessContext.Provider value={value}>{children}</AdminAccessContext.Provider>;
}

export function useAdminAccess(): AdminAccessContextValue {
  const ctx = useContext(AdminAccessContext);
  if (!ctx) {
    throw new Error('useAdminAccess must be used within AdminAccessProvider');
  }
  return ctx;
}
